// EditEntryModal.jsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../../lib/api';
import EditEntryWeightModal from './EditEntryWeightsModal';
import { useTranslation } from 'react-i18next';

const EditEntryModal = ({ isOpen, onClose, record, locationKey, onUpdated }) => {
  const { t } = useTranslation();

  // Safe date formatting
  const safeDate = (value) => {
    if (!value) return new Date().toISOString().split('T')[0];
    try {
      return new Date(value).toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  // Local states
  const [quantityEntered, setQuantityEntered] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [fixedWeight, setFixedWeight] = useState(null);
  const [notes, setNotes] = useState('');
  const [enteredAt, setEnteredAt] = useState(safeDate());

  const [showWeightsModal, setShowWeightsModal] = useState(false);

  // Load initial values
  useEffect(() => {
    if (record) {
      setQuantityEntered(record.quantityEntered || 0);
      setTotalWeight(record.totalWeight || 0);
      setFixedWeight(record.fixedWeight ?? null);
      setNotes(record.notes || '');
      setEnteredAt(safeDate(record.enteredAt));
    }
  }, [record]);

  if (!isOpen || !record) return null;

  // Save only notes + date (SAFE MODE)
  const handleSafeUpdate = async () => {
    try {
      // Agregamos T12:00:00 para asegurar que la fecha sea tratada como mediodía local
      // y no retroceda al convertir a ISO string (ISO usa UTC-0)
      const dateWithTime = enteredAt + 'T12:00:00';

      const payload = {
        notes,
        // Using enteredAt to match ProductEntry field naming
        enteredAt: new Date(dateWithTime).toISOString()
      };

      // Updated path to match /inventory/entries controller
      await api.put(
        `/inventory/entries/${record.id || record._id}?location=${locationKey}`,
        payload
      );

      onUpdated?.();
      onClose();
    } catch (err) {
      console.error(err);
      alert(t('errorUpdatingEntry'));
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999]">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-lg p-6 relative">

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-700 dark:text-gray-300 hover:text-black"
          >
            <X size={22} />
          </button>

          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
            {t('editEntry')}
          </h2>

          {/* Read-only product info */}
          <div className="space-y-1 mb-4 text-sm text-gray-600 dark:text-gray-300">
            <p><strong>{t('product')}:</strong> {record.productName}</p>
            <p><strong>{t('brand')}:</strong> {record.brandName}</p>
            <p><strong>{t('supplier')}:</strong> {record.supplierName}</p>
          </div>

          {/* Editable fields */}
          <div className="space-y-3">

            {/* Quantity disabled ALWAYS */}
            <div>
              <label className="text-sm font-medium">{t('quantity')}</label>
              <input
                disabled
                type="number"
                value={quantityEntered}
                className="w-full mt-1 px-3 py-2 rounded-lg border opacity-50 cursor-not-allowed dark:bg-gray-800 dark:text-white"
              />
              <p className="text-xs text-gray-500">{t('disabledQuantityEdit')}</p>
            </div>

            {/* Base weight disabled ALWAYS */}
            <div>
              <label className="text-sm font-medium">{t('baseWeightLabel')}</label>
              <input
                disabled
                type="number"
                value={fixedWeight ?? ''}
                className="w-full mt-1 px-3 py-2 rounded-lg border opacity-50 cursor-not-allowed dark:bg-gray-800 dark:text-white"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium">{t('notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border dark:bg-gray-800 dark:text-white"
              />
            </div>

            {/* Date */}
            <div>
              <label className="text-sm font-medium">{t('date')}</label>
              <input
                type="date"
                value={enteredAt}
                onChange={(e) => setEnteredAt(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border dark:bg-gray-800 dark:text-white"
              />
            </div>

            {/* 🔒 FOR SAFETY → Hide the Edit Boxes button ALWAYS */}
            {/* Button removed temporarily for inventory safety */}

          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-gray-300 dark:bg-gray-700 text-black dark:text-white"
            >
              {t('cancel')}
            </button>

            <button
              onClick={handleSafeUpdate}
              className="px-4 py-2 rounded-lg bg-black text-white dark:bg-white dark:text-black hover:scale-105 transition"
            >
              Update
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default EditEntryModal;
